import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, LogOut, Users, MessageSquare, Trash2, Plus, Copy, Edit, Key, X, Settings, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NavigationMenu from '@/components/NavigationMenu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhook`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [selectedAccountForRules, setSelectedAccountForRules] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [accountForm, setAccountForm] = useState({
    name: '',
    phone_number_id: '',
    api_token: '',
    user_id: '',
    default_response_type: 'silent',
    default_response_message: '',
    default_forward_url: ''
  });

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.id}`, accountForm);
        toast.success('Akun berhasil diperbarui!');
      } else {
        await axios.post(`${API}/accounts`, accountForm);
        toast.success('Akun berhasil dibuat!');
      }
      setShowAccountDialog(false);
      resetAccountForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan akun');
    } finally {
      setLoading(false);
    }
  };

  const openEditAccountDialog = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name,
      phone_number_id: account.phone_number_id,
      api_token: account.api_token,
      user_id: account.user_id || '',
      default_response_type: account.default_response_type,
      default_response_message: account.default_response_message || '',
      default_forward_url: account.default_forward_url || ''
    });
    setShowAccountDialog(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingUser) {
        const updateData = { email: userForm.email, role: userForm.role };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully!');
      } else {
        await axios.post(`${API}/users`, userForm);
        toast.success('User created successfully!');
      }
      setShowUserDialog(false);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await axios.delete(`${API}/accounts/${accountId}`);
      toast.success('Account deleted successfully!');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/users/${resetUserId}/reset-password`, { password: newPassword });
      toast.success('Password reset successfully!');
      setShowResetDialog(false);
      setResetUserId(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openEditUserDialog = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      password: '',
      role: user.role
    });
    setShowUserDialog(true);
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(WEBHOOK_URL);
    toast.success('Webhook URL copied to clipboard!');
  };

  const resetAccountForm = () => {
    setEditingAccount(null);
    setAccountForm({
      name: '',
      phone_number_id: '',
      api_token: '',
      user_id: '',
      default_response_type: 'silent',
      default_response_message: '',
      default_forward_url: ''
    });
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      password: '',
      role: 'user'
    });
  };

  const openRulesManagement = (account) => {
    // Store account info in sessionStorage for admin to manage rules
    sessionStorage.setItem('adminManageAccount', JSON.stringify({
      id: account.id,
      name: account.name,
      phone_number_id: account.phone_number_id
    }));
    // Navigate to user dashboard with special flag
    navigate('/dashboard', { state: { adminMode: true, accountId: account.id } });
  };

  const MobileNav = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="lg:hidden" data-testid="mobile-menu">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64 p-0">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800">WA Chatbot</h2>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
          </div>
          <div className="flex-1 p-4">
            <div className="mb-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Logged in as</p>
              <p className="text-sm font-medium text-gray-800 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="p-4 border-t">
            <Button
              onClick={logout}
              variant="outline"
              className="w-full border-2 border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <NavigationMenu />
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800" data-testid="admin-dashboard-title">
                  Dashboard Admin
                </h1>
                <p className="text-sm text-gray-600 hidden sm:block">Selamat datang, {user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs defaultValue="accounts" className="space-y-6">
          <TabsList className="bg-white shadow-sm p-1 rounded-lg w-full sm:w-auto grid grid-cols-2">
            <TabsTrigger value="accounts" className="rounded-md px-4 sm:px-6" data-testid="accounts-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Akun <span className="hidden sm:inline">WhatsApp</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-md px-4 sm:px-6" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Manajemen <span className="hidden sm:inline">User</span>
            </TabsTrigger>
          </TabsList>

          {/* Accounts Tab */}
          <TabsContent value="accounts" className="space-y-6">
            {/* Webhook Info */}
            <Card className="border-2 border-purple-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Konfigurasi Webhook</CardTitle>
                <CardDescription className="text-sm">Gunakan URL ini di pengaturan Meta WhatsApp Business</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input value={WEBHOOK_URL} readOnly className="font-mono text-xs sm:text-sm" data-testid="webhook-url" />
                  <Button onClick={copyWebhookUrl} variant="outline" className="sm:w-auto" data-testid="copy-webhook-button">
                    <Copy className="w-4 h-4 mr-2" />
                    Salin
                  </Button>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-3">
                  <strong>Token Verifikasi:</strong> <code className="bg-gray-100 px-2 py-1 rounded text-xs">wanesiabot</code>
                </p>
              </CardContent>
            </Card>

            {/* Add Account Button & List */}
            <Card className="border-2 border-purple-200 shadow-lg">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle className="text-lg sm:text-xl">Akun WhatsApp ({accounts.length})</CardTitle>
                    <CardDescription className="text-sm">Semua akun yang terdaftar</CardDescription>
                  </div>
                  <Dialog open={showAccountDialog} onOpenChange={(open) => { setShowAccountDialog(open); if (!open) resetAccountForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 w-full sm:w-auto" data-testid="add-account-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Akun
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingAccount ? 'Edit Akun WhatsApp' : 'Tambah Akun WhatsApp Baru'}</DialogTitle>
                        <DialogDescription>{editingAccount ? 'Perbarui informasi akun WhatsApp' : 'Daftarkan akun WhatsApp Business baru'}</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAccountSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="name">Nama Akun</Label>
                            <Input
                              id="name"
                              value={accountForm.name}
                              onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                              placeholder="Akun Penjualan"
                              required
                              data-testid="account-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone_number_id">Phone Number ID</Label>
                            <Input
                              id="phone_number_id"
                              value={accountForm.phone_number_id}
                              onChange={(e) => setAccountForm({ ...accountForm, phone_number_id: e.target.value })}
                              placeholder="123456789012345"
                              required
                              data-testid="phone-number-id-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="api_token">Access Token</Label>
                          <Input
                            id="api_token"
                            type="password"
                            value={accountForm.api_token}
                            onChange={(e) => setAccountForm({ ...accountForm, api_token: e.target.value })}
                            placeholder="Masukkan WhatsApp API Token"
                            required
                            data-testid="api-token-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">Token akan diverifikasi dengan Meta API</p>
                        </div>
                        <div>
                          <Label htmlFor="user_id">Assign to User (Optional)</Label>
                          <Select
                            value={accountForm.user_id || "none"}
                            onValueChange={(value) => setAccountForm({ ...accountForm, user_id: value === "none" ? "" : value })}
                          >
                            <SelectTrigger data-testid="user-select">
                              <SelectValue placeholder="Select a user" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No assignment</SelectItem>
                              {users.filter(u => u.role === 'user').map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="default_response_type">Default Response When No Rule Matches</Label>
                          <Select
                            value={accountForm.default_response_type}
                            onValueChange={(value) => setAccountForm({ ...accountForm, default_response_type: value })}
                          >
                            <SelectTrigger data-testid="default-response-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="silent">Silent (No response)</SelectItem>
                              <SelectItem value="custom">Custom Message</SelectItem>
                              <SelectItem value="forward">Forward to Webhook</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {accountForm.default_response_type === 'custom' && (
                          <div>
                            <Label htmlFor="default_response_message">Default Message</Label>
                            <Textarea
                              id="default_response_message"
                              value={accountForm.default_response_message}
                              onChange={(e) => setAccountForm({ ...accountForm, default_response_message: e.target.value })}
                              placeholder="Sorry, I didn't understand that."
                              data-testid="default-message-input"
                            />
                          </div>
                        )}
                        {accountForm.default_response_type === 'forward' && (
                          <div>
                            <Label htmlFor="default_forward_url">Forward Webhook URL</Label>
                            <Input
                              id="default_forward_url"
                              value={accountForm.default_forward_url}
                              onChange={(e) => setAccountForm({ ...accountForm, default_forward_url: e.target.value })}
                              placeholder="https://your-webhook-url.com/webhook"
                              data-testid="forward-url-input"
                            />
                          </div>
                        )}
                        <DialogFooter>
                          <Button type="submit" disabled={loading} data-testid="submit-account-button">
                            {loading ? (editingAccount ? 'Menyimpan...' : 'Memverifikasi & Membuat...') : (editingAccount ? 'Perbarui Akun' : 'Buat Akun')}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {accounts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8" data-testid="no-accounts-message">No accounts registered yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nama Akun</TableHead>
                          <TableHead className="hidden lg:table-cell">Nama Terverifikasi</TableHead>
                          <TableHead className="hidden md:table-cell">Nomor Telepon</TableHead>
                          <TableHead className="hidden sm:table-cell">Ditugaskan Ke</TableHead>
                          <TableHead className="text-right">Aksi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="accounts-table">
                        {accounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{account.name}</div>
                                <div className="md:hidden text-xs text-gray-500 mt-1">
                                  {account.display_phone_number || account.phone_number_id}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <span className="text-sm text-gray-700">{account.verified_name || '-'}</span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <div className="text-sm">
                                <div className="font-medium">{account.display_phone_number || '-'}</div>
                                <div className="text-xs text-gray-500 font-mono">{account.phone_number_id}</div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-sm">
                              {account.user_id
                                ? users.find(u => u.id === account.user_id)?.email || 'Unknown'
                                : 'Belum Ditugaskan'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openRulesManagement(account)}
                                  className="text-green-600 hover:text-green-700"
                                  title="Kelola Aturan"
                                  data-testid={`manage-rules-${account.id}`}
                                >
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditAccountDialog(account)}
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`edit-account-${account.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-account-${account.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will delete the account and all associated chatbot rules. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteAccount(account.id)} className="bg-red-600 hover:bg-red-700">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-2 border-purple-200 shadow-lg">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle className="text-lg sm:text-xl">User Management ({users.length})</CardTitle>
                    <CardDescription className="text-sm">Manage users and permissions</CardDescription>
                  </div>
                  <Dialog open={showUserDialog} onOpenChange={(open) => { setShowUserDialog(open); if (!open) resetUserForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 w-full sm:w-auto" data-testid="add-user-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
                        <DialogDescription>
                          {editingUser ? 'Update user information' : 'Add a new user to the system'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleUserSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            placeholder="user@example.com"
                            required
                            data-testid="user-email-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="password">Password {editingUser && '(leave empty to keep current)'}</Label>
                          <Input
                            id="password"
                            type="password"
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            placeholder="••••••••"
                            required={!editingUser}
                            data-testid="user-password-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="role">Role</Label>
                          <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                            <SelectTrigger data-testid="user-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={loading} data-testid="submit-user-button">
                            {loading ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No users found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead className="hidden sm:table-cell">Role</TableHead>
                          <TableHead className="hidden md:table-cell">Created</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="users-table">
                        {users.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{u.email}</div>
                                <div className="sm:hidden">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                                    u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {u.role}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {u.role}
                              </span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditUserDialog(u)}
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setResetUserId(u.id); setShowResetDialog(true); }}
                                  className="text-orange-600 hover:text-orange-700"
                                  data-testid={`reset-password-${u.id}`}
                                >
                                  <Key className="w-4 h-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-user-${u.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete this user. Their assigned accounts will be unassigned. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteUser(u.id)} className="bg-red-600 hover:bg-red-700">
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Enter a new password for this user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetDialog(false); setNewPassword(''); }}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} data-testid="confirm-reset-password">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
